import type { RequestContext } from '@mastra/core/request-context';

import type {
  ClientOptions,
  StoredAgentResponse,
  UpdateStoredAgentParams,
  DeleteStoredAgentResponse,
  StoredAgentDependentsResponse,
  AgentVersionResponse,
  ListAgentVersionsParams,
  ListAgentVersionsResponse,
  CreateAgentVersionParams,
  ActivateAgentVersionResponse,
  CompareVersionsResponse,
  DeleteAgentVersionResponse,
  FavoriteToggleResponse,
  ExportStoredAgentParams,
  ExportStoredAgentResponse,
  OpenStoredAgentChangeRequestParams,
  OpenStoredAgentChangeRequestResponse,
} from '../types';
import { requestContextQueryString } from '../utils';

import { BaseResource } from './base';

/**
 * Resource for interacting with a specific stored agent
 */
export class StoredAgent extends BaseResource {
  constructor(
    options: ClientOptions,
    private storedAgentId: string,
  ) {
    super(options);
  }

  /**
   * Retrieves details about the stored agent
   * @param requestContext - Optional request context to pass as query parameter
   * @param options - Optional options like status filter
   * @returns Promise containing stored agent details
   */
  details(
    requestContext?: RequestContext | Record<string, any>,
    options?: { status?: 'draft' | 'published' | 'archived' },
  ): Promise<StoredAgentResponse> {
    const contextString = requestContextQueryString(requestContext);
    const statusParam = options?.status ? `status=${options.status}` : '';
    const url = `/stored/agents/${encodeURIComponent(this.storedAgentId)}${contextString}${statusParam ? `${contextString ? '&' : '?'}${statusParam}` : ''}`;
    return this.request(url);
  }

  /**
   * Updates the stored agent with the provided fields
   * @param params - Fields to update
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the updated stored agent
   */
  update(
    params: UpdateStoredAgentParams,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<StoredAgentResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}${requestContextQueryString(requestContext)}`,
      {
        method: 'PATCH',
        body: params,
      },
    );
  }

  /**
   * Exports deterministic JSON for this agent without mutating storage.
   */
  export(params: ExportStoredAgentParams): Promise<ExportStoredAgentResponse> {
    return this.request(`/stored/agents/${encodeURIComponent(this.storedAgentId)}/export`, {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Opens a source-provider change request for deterministic agent JSON without mutating storage.
   */
  openChangeRequest(params: OpenStoredAgentChangeRequestParams): Promise<OpenStoredAgentChangeRequestResponse> {
    return this.request(`/stored/agents/${encodeURIComponent(this.storedAgentId)}/change-request`, {
      method: 'POST',
      body: params,
    });
  }

  /**
   * Deletes the stored agent
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing deletion confirmation
   */
  delete(requestContext?: RequestContext | Record<string, any>): Promise<DeleteStoredAgentResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}${requestContextQueryString(requestContext)}`,
      {
        method: 'DELETE',
      },
    );
  }

  /**
   * Lists other stored agents that reference this agent as a sub-agent.
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the list of dependent agents and a hidden count
   *          for cross-workspace private dependents (only non-zero when this agent is public).
   */
  dependents(requestContext?: RequestContext | Record<string, any>): Promise<StoredAgentDependentsResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/dependents${requestContextQueryString(requestContext)}`,
    );
  }

  // ==========================================================================
  // Favorite Methods (EE feature)
  // ==========================================================================

  /**
   * Favorites this agent for the calling user. Idempotent.
   * Requires the `agent.favorites` builder feature flag to be enabled on the server.
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the new favorited state and updated favorite count
   */
  favorite(requestContext?: RequestContext | Record<string, any>): Promise<FavoriteToggleResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/favorite${requestContextQueryString(requestContext)}`,
      {
        method: 'PUT',
      },
    );
  }

  /**
   * Unfavorites this agent for the calling user. Idempotent.
   * Requires the `agent.favorites` builder feature flag to be enabled on the server.
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the new favorited state and updated favorite count
   */
  unfavorite(requestContext?: RequestContext | Record<string, any>): Promise<FavoriteToggleResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/favorite${requestContextQueryString(requestContext)}`,
      {
        method: 'DELETE',
      },
    );
  }

  // ==========================================================================
  // Version Methods
  // ==========================================================================

  /**
   * Lists all versions for this stored agent
   * @param params - Optional pagination and sorting parameters
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing paginated list of versions
   */
  listVersions(
    params?: ListAgentVersionsParams,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<ListAgentVersionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.set('page', String(params.page));
    if (params?.perPage !== undefined) queryParams.set('perPage', String(params.perPage));
    if (params?.orderBy) {
      if (params.orderBy.field) {
        queryParams.set('orderBy[field]', params.orderBy.field);
      }
      if (params.orderBy.direction) {
        queryParams.set('orderBy[direction]', params.orderBy.direction);
      }
    }

    const queryString = queryParams.toString();
    const contextString = requestContextQueryString(requestContext);
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions${queryString ? `?${queryString}` : ''}${contextString ? `${queryString ? '&' : '?'}${contextString.slice(1)}` : ''}`,
    );
  }

  /**
   * Creates a new version snapshot for this stored agent
   * @param params - Optional name and change message for the version
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the created version
   */
  createVersion(
    params?: CreateAgentVersionParams,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<AgentVersionResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions${requestContextQueryString(requestContext)}`,
      {
        method: 'POST',
        body: params || {},
      },
    );
  }

  /**
   * Retrieves a specific version by its ID
   * @param versionId - The UUID of the version to retrieve
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the version details
   */
  getVersion(versionId: string, requestContext?: RequestContext | Record<string, any>): Promise<AgentVersionResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions/${encodeURIComponent(versionId)}${requestContextQueryString(requestContext)}`,
    );
  }

  /**
   * Activates a specific version, making it the active version for this agent
   * @param versionId - The UUID of the version to activate
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing activation confirmation with success status, message, and active version ID
   */
  activateVersion(
    versionId: string,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<ActivateAgentVersionResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions/${encodeURIComponent(versionId)}/activate${requestContextQueryString(requestContext)}`,
      {
        method: 'POST',
      },
    );
  }

  /**
   * Restores a version by creating a new version with the same configuration
   * @param versionId - The UUID of the version to restore
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the newly created version
   */
  restoreVersion(
    versionId: string,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<AgentVersionResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions/${encodeURIComponent(versionId)}/restore${requestContextQueryString(requestContext)}`,
      {
        method: 'POST',
      },
    );
  }

  /**
   * Deletes a specific version
   * @param versionId - The UUID of the version to delete
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise that resolves with deletion response
   */
  deleteVersion(
    versionId: string,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<DeleteAgentVersionResponse> {
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions/${encodeURIComponent(versionId)}${requestContextQueryString(requestContext)}`,
      {
        method: 'DELETE',
      },
    );
  }

  /**
   * Compares two versions and returns their differences
   * @param fromId - The UUID of the source version
   * @param toId - The UUID of the target version
   * @param requestContext - Optional request context to pass as query parameter
   * @returns Promise containing the comparison results
   */
  compareVersions(
    fromId: string,
    toId: string,
    requestContext?: RequestContext | Record<string, any>,
  ): Promise<CompareVersionsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.set('from', fromId);
    queryParams.set('to', toId);

    const contextString = requestContextQueryString(requestContext);
    return this.request(
      `/stored/agents/${encodeURIComponent(this.storedAgentId)}/versions/compare?${queryParams.toString()}${contextString ? `&${contextString.slice(1)}` : ''}`,
    );
  }
}
